import { useState, useEffect, useCallback } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import images from "../data/slider.json";

const ImageSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [textKey, setTextKey] = useState(0);

  const goToSlide = useCallback(
    (index: number) => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setTextKey((k) => k + 1);
      setCurrentIndex(index);
      setTimeout(() => setIsTransitioning(false), 1000);
    },
    [isTransitioning],
  );

  const nextSlide = useCallback(() => {
    goToSlide((currentIndex + 1) % images.length);
  }, [currentIndex, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((currentIndex - 1 + images.length) % images.length);
  }, [currentIndex, goToSlide]);

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(nextSlide, 3000);
    return () => clearInterval(interval);
  }, [isPaused, nextSlide]);

  return (
    <section
      id="home"
      className="relative w-full h-[55vh] sm:h-[65vh] md:h-[85vh] lg:h-screen overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Slides */}
      <div
        className="flex h-full transition-transform duration-[1200ms] ease-[cubic-bezier(0.65,0,0.35,1)]"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {images.map((image, index) => (
          <div key={index} className="w-full flex-shrink-0 h-full relative">
            <img
              src={image.url}
              alt={image.title}
              className="w-full h-full object-cover object-center"
              loading={index === 0 ? "eager" : "lazy"}
            />
          </div>
        ))}
      </div>

      {/* Bottom Content Bar */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <div className="bg-gradient-to-t from-black/80 via-black/50 to-transparent pt-16 pb-6 md:pb-10">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div key={textKey} className="max-w-3xl space-y-3 md:space-y-4">
              {/* Pill Badge */}
              <div className="animate-fadeInDown" style={{ animationDelay: "0.05s" }}>
                <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-black/30 backdrop-blur-md border border-white/10 text-white">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-400" />
                  </span>
                  <span className="text-xs md:text-sm font-medium tracking-wide">
                    Orange City Sport Shooting Club
                  </span>
                </div>
              </div>

              {/* Title */}
              <div
                className="animate-fadeInUp"
                style={{ animationDelay: "0.1s" }}
              >
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white gowun-batang leading-[1.1] tracking-tight">
                  {images[currentIndex].title.split(" ").map((word, i, arr) => (
                    <span
                      key={i}
                      className={i === arr.length - 1 ? "text-orange-400" : ""}
                    >
                      {word}{" "}
                    </span>
                  ))}
                </h1>
              </div>

              {/* Accent Line */}
              <div
                className="animate-fadeInUp"
                style={{ animationDelay: "0.2s" }}
              >
                <div className="w-12 h-1 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full" />
              </div>

              {/* Subtitle */}
              <div
                className="animate-fadeInUp"
                style={{ animationDelay: "0.25s" }}
              >
                <p className="text-base sm:text-lg md:text-xl text-white/70 font-light max-w-xl leading-relaxed">
                  {images[currentIndex].subtitle}
                </p>
              </div>

              {/* Dots + Counter */}
              <div
                className="animate-fadeInUp flex items-center gap-4 pt-2"
                style={{ animationDelay: "0.35s" }}
              >
                <div className="hidden sm:flex items-center gap-3">
                  <span className="text-white/90 text-sm font-mono font-medium">
                    {String(currentIndex + 1).padStart(2, "0")}
                  </span>
                  <div className="w-12 h-px bg-white/20 relative">
                    <div
                      className="absolute inset-y-0 left-0 bg-orange-400 transition-all duration-500"
                      style={{
                        width: `${((currentIndex + 1) / images.length) * 100}%`,
                      }}
                    />
                  </div>
                  <span className="text-white/40 text-sm font-mono">
                    {String(images.length).padStart(2, "0")}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`transition-all duration-500 rounded-full ${
                        currentIndex === index
                          ? "w-8 h-2 bg-orange-400"
                          : "w-2 h-2 bg-white/30 hover:bg-white/50"
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/20 backdrop-blur-sm border border-white/10 text-white/70 flex items-center justify-center hover:bg-black/40 hover:text-white hover:scale-105 transition-all duration-300 group z-10"
        aria-label="Previous slide"
      >
        <ChevronLeftIcon className="w-5 h-5 md:w-6 md:h-6 group-hover:-translate-x-0.5 transition-transform" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/20 backdrop-blur-sm border border-white/10 text-white/70 flex items-center justify-center hover:bg-black/40 hover:text-white hover:scale-105 transition-all duration-300 group z-10"
        aria-label="Next slide"
      >
        <ChevronRightIcon className="w-5 h-5 md:w-6 md:h-6 group-hover:translate-x-0.5 transition-transform" />
      </button>
    </section>
  );
};

export default ImageSlider;
